import type { SxProps, Theme } from '@mui/material';
import { DOCK_CSS } from '../components/layout/floatingDockLayout';

/** Classe CSS — evita corte de labels MUI no mobile (ver index.css). */
export const MOBILE_FORM_SURFACE_CLASS = 'mobile-form-surface';

export const mobileFullScreenDialogPaperSx = {
  m: 0,
  borderRadius: 0,
  boxSizing: 'border-box',
  pt: 'env(safe-area-inset-top, 0px)',
  pb: 0,
  pl: 'env(safe-area-inset-left, 0px)',
  pr: 'env(safe-area-inset-right, 0px)',
  height: 'var(--ui-viewport-height, 100dvh)',
  maxHeight: 'var(--ui-viewport-height, 100dvh)',
  width: '100%',
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
} as const;

/**
 * Modais clínicos desktop (agenda+paciente, receituário, evolução, orçamento):
 * ocupa quase toda a área útil à direita do menu lateral recolhido.
 */
const CLINIC_DIALOG_LEFT_OFFSET_PX = 104;
const CLINIC_DIALOG_TOP_OFFSET_PX = 28;
const CLINIC_DIALOG_EDGE_PX = 12;

/** Compensa `html { zoom: var(--ui-density-zoom) }` em desktop (ver index.css). */
const UI_DENSITY_ZOOM = 'var(--ui-density-zoom, 1)';
const unzoom = (px: number) => `calc(${px}px / ${UI_DENSITY_ZOOM})`;

export const clinicLargeFormDialogPaperSx = (fullScreen: boolean) => {
  if (fullScreen) {
    return {
      borderRadius: 0,
      width: '100%',
      maxWidth: '100%',
      height: '100%',
      maxHeight: '100%',
      margin: 0,
      display: 'flex',
      flexDirection: 'column' as const,
      overflow: 'hidden',
    };
  }

  const widthExpr = `calc(100vw / ${UI_DENSITY_ZOOM} - ${unzoom(CLINIC_DIALOG_LEFT_OFFSET_PX)} - ${unzoom(CLINIC_DIALOG_EDGE_PX)})`;
  const bottomClearanceExpr = `calc((${DOCK_CSS.bottomClearance}) / ${UI_DENSITY_ZOOM})`;
  const heightExpr = `calc(var(--ui-viewport-height, 100dvh) - ${unzoom(CLINIC_DIALOG_TOP_OFFSET_PX)} - ${bottomClearanceExpr})`;

  return {
    borderRadius: 3,
    boxSizing: 'border-box' as const,
    width: widthExpr,
    maxWidth: widthExpr,
    height: heightExpr,
    maxHeight: heightExpr,
    margin: `${unzoom(CLINIC_DIALOG_TOP_OFFSET_PX)} ${unzoom(CLINIC_DIALOG_EDGE_PX)} ${bottomClearanceExpr} ${unzoom(CLINIC_DIALOG_LEFT_OFFSET_PX)}`,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
  };
};

/** Agenda: editar consulta + painel do paciente (layout em duas colunas). */
export const clinicSplitWorkspaceDialogPaperSx = (fullScreen: boolean) => ({
  ...clinicLargeFormDialogPaperSx(fullScreen),
  flexDirection: 'row' as const,
});

/** Alinha o paper ao canto útil (menu lateral), não ao centro do viewport. */
export const clinicFormDialogRootSx = {
  '& .MuiDialog-container': {
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
} as const;

export const mobileDialogFormContentSx = (isMobile: boolean): SxProps<Theme> =>
  isMobile
    ? {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'visible',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
        pt: 2.5,
        scrollPaddingTop: 16,
      }
    : {};

export const mobileFormFieldLabelProps = (isMobile: boolean) =>
  isMobile ? ({ shrink: true } as const) : undefined;
